import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import {
  Search, Loader2, MapPin, Building2, Phone, ExternalLink, ShieldCheck, GitCompare,
  TrendingUp, TrendingDown, MessageSquareWarning, ThumbsUp, Wallet, Receipt,
  User, Mail, CalendarClock, FileDown, CheckCircle2, ClipboardList, UserPlus, Check, Share2, Globe, Landmark, FileText, ImagePlus, X,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { computeHealth } from "../../lib/healthScore";
import ProspectCharts from "./ProspectCharts";
import FraudRiskCard from "./FraudRiskCard";
import LocationCompare from "./LocationCompare";

const normName = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
const withTimeout = (p, ms) => Promise.race([p, new Promise((res) => setTimeout(() => res(null), ms))]);

const inputCls =
  "w-full rounded-xl border border-[#1e3d3a]/15 bg-[#eae7de]/60 px-4 py-3 text-sm text-[#1e3d3a] placeholder:text-[#1e3d3a]/35 focus:border-[#c9a961] focus:outline-none focus:ring-2 focus:ring-[#c9a961]/20 transition-colors duration-300";

const money = (n) =>
  typeof n === "number" && !Number.isNaN(n)
    ? n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 })
    : "—";

const INDUSTRIES = ["", "Hospitality", "Retail", "Construction", "Automotive", "Healthcare", "Home Services", "Manufacturing", "Professional Services"];

function ReviewCard({ r, tone }) {
  const border = tone === "neg" ? "border-l-[#b23a48]" : "border-l-[#4b7a3a]";
  return (
    <div className={`rounded-r-xl border border-[#1e3d3a]/10 border-l-2 ${border} bg-white px-3 py-2`}>
      <p className="text-xs text-[#1e3d3a]/80 leading-relaxed">“{r.excerpt}”</p>
      <p className="mt-1 text-[9px] uppercase tracking-[0.16em] text-[#1e3d3a]/40">
        {[r.theme, r.source, r.date_hint].filter(Boolean).join(" · ")}
      </p>
    </div>
  );
}

// Business Health Score from public signals (shared with Market scan + Leads).
function HealthGauge({ data }) {
  if (!data) return null;
  const R = 30, C = 2 * Math.PI * R, off = C * (1 - data.score / 100);
  return (
    <div data-testid="lookup-health-score" className="group relative flex flex-col items-center cursor-help">
      <div className="text-[9px] uppercase tracking-[0.2em] text-[#1e3d3a]/45 mb-1">Health score</div>
      <div className="relative h-[78px] w-[78px]">
        <svg viewBox="0 0 78 78" className="h-[78px] w-[78px] -rotate-90">
          <circle cx="39" cy="39" r={R} fill="none" stroke="#1e3d3a14" strokeWidth="7" />
          <circle cx="39" cy="39" r={R} fill="none" stroke={data.color} strokeWidth="7" strokeLinecap="round" strokeDasharray={C} strokeDashoffset={off} />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="font-head text-2xl leading-none" style={{ color: data.color }}>{data.score}</span>
          <span className="text-[9px] text-[#1e3d3a]/45 leading-none mt-0.5">of 100</span>
        </div>
      </div>
      <div className="mt-1 text-[11px] font-semibold uppercase tracking-wide" style={{ color: data.color }}>{data.grade} · {data.label}</div>
      {/* Breakdown tooltip */}
      <div data-testid="lookup-health-tooltip" className="pointer-events-none absolute top-full right-0 z-30 mt-2 w-60 rounded-xl border border-[#1e3d3a]/12 bg-white p-3 text-left opacity-0 shadow-xl transition-opacity duration-200 group-hover:opacity-100">
        <div className="text-[9px] uppercase tracking-[0.18em] text-[#a3823f] mb-1.5">How this score is built</div>
        <ul className="space-y-1">
          {data.factors.map((ftxt, i) => (
            <li key={i} className="flex items-start gap-1.5 text-[11px] text-[#1e3d3a]/70">
              <span className="mt-1 h-1 w-1 shrink-0 rounded-full" style={{ background: data.color }} />{ftxt}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}


function ProfileCard({ p, onPick, active }) {
  return (
    <button
      type="button"
      data-testid={`lookup-candidate-${(p.company_name || "").replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
      onClick={() => onPick(p)}
      className={`w-full text-left rounded-2xl border p-4 transition-colors duration-300 ${active ? "border-[#c9a961] bg-[#c9a961]/8" : "border-[#1e3d3a]/12 bg-white hover:border-[#1e3d3a]/30"}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h4 className="font-head text-base text-[#1e3d3a] truncate">{p.company_name}</h4>
          {p.location && (
            <p className="mt-1 text-xs text-[#1e3d3a]/50 flex items-center gap-1">
              <MapPin className="h-3 w-3" /> {p.location}
            </p>
          )}
        </div>
        {p.bbb_rating
          ? <span className="shrink-0 text-xs rounded-full border border-[#1e3d3a]/15 px-2 py-1 text-[#1e3d3a]/70">BBB {p.bbb_rating}</span>
          : <span className="shrink-0 text-xs rounded-full border border-dashed border-[#1e3d3a]/20 px-2 py-1 text-[#1e3d3a]/45">Not rated</span>}
      </div>
    </button>
  );
}

function StatTile({ label, value, tone, icon: Icon, sub }) {
  const color = tone === "pos" ? "text-[#4b7a3a]" : tone === "neg" ? "text-[#b23a48]" : "text-[#1e3d3a]";
  return (
    <div className="rounded-2xl border border-[#1e3d3a]/12 bg-white p-4">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-[#1e3d3a]/50">
        {Icon && <Icon className="h-3.5 w-3.5 text-[#a3823f]" />} {label}
      </div>
      <div className={`mt-2 font-head text-2xl ${color}`}>{value}</div>
      {sub && <div className="mt-1 text-[11px] text-[#1e3d3a]/45">{sub}</div>}
    </div>
  );
}

function SectionNo({ n, children }) {
  return (
    <div className="flex items-center gap-2.5 pt-1" data-testid={`section-no-${n}`}>
      <span className="font-head text-lg text-[#1e3d3a] tabular-nums leading-none">{String(n).padStart(2, "0")}</span>
      <span className="text-[10px] uppercase tracking-[0.24em] text-[#a3823f] font-bold">{children}</span>
      <span className="h-px flex-1 bg-[#1e3d3a]/12" />
    </div>
  );
}

export default function BusinessLookup({ prefill }) {
  const [mode, setMode] = useState("name");
  const [name, setName] = useState("");
  const [location, setLocation] = useState("");
  const [website, setWebsite] = useState("");
  const [industry, setIndustry] = useState("");
  const [searching, setSearching] = useState(false);
  const [candidates, setCandidates] = useState(null);
  const [notice, setNotice] = useState(null);
  const [selected, setSelected] = useState(null);
  const [fin, setFin] = useState(null);
  const [finLoading, setFinLoading] = useState(false);
  const [reported, setReported] = useState(null);
  const [liveDetails, setLiveDetails] = useState(null);
  const [sec, setSec] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [cobrandLogo, setCobrandLogo] = useState(null);
  const logoInputRef = useRef(null);
  const [addingLead, setAddingLead] = useState(false);
  const [leadAdded, setLeadAdded] = useState(false);
  const [packBusy, setPackBusy] = useState(false);
  const [convertOpen, setConvertOpen] = useState(false);
  const [convertBusy, setConvertBusy] = useState(false);
  const [converted, setConverted] = useState(false);
  const [cvEmail, setCvEmail] = useState("");
  const [cvOwner, setCvOwner] = useState("");
  const [cvPhone, setCvPhone] = useState("");
  const [scope, setScope] = useState({ kind: "location", loc: null }); // loc = profile row; kind "combined" = all locations
  const [comparing, setComparing] = useState(false);
  const [compareData, setCompareData] = useState(null);
  const [finCache, setFinCache] = useState({}); // location key -> financials (shared by compare)

  // Every BBB profile that belongs to this company: the picked one, its
  // alternate_locations, and any other candidate with the same name.
  const locationsFor = (p) => {
    if (!p) return [];
    const lk = (x) => `${(x.url || "").toLowerCase()}|${(x.location || "").toLowerCase().trim()}`;
    const out = [p];
    const seen = new Set([lk(p)]);
    [...(p.alternate_locations || []), ...((candidates || []).filter((c) => c !== p && normName(c.company_name) === normName(p.company_name)))]
      .forEach((a) => { const k = lk(a); if ((a.url || a.location) && !seen.has(k)) { seen.add(k); out.push(a); } });
    return out;
  };

  const runFinancials = async (p, sc, ctx = {}) => {
    setFinLoading(true);
    setFin(null);
    const locs = locationsFor(p);
    const active = sc.kind === "combined" ? p : (sc.loc || p);
    try {
      const { data } = await api.post("/bbb/business-financials", {
        company_name: p.company_name,
        location: sc.kind === "combined" ? (p.location || location || null) : (active.location || location || null),
        bbb_rating: active.bbb_rating || null,
        complaints: sc.kind === "combined"
          ? locs.reduce((s, l) => (typeof l.complaints === "number" ? s + l.complaints : s), 0) || null
          : (typeof active.complaints === "number" ? active.complaints : null),
        industry: industry || null,
        website: active.website || website || p.website || null,
        bbb_url: active.url && active.url.includes("bbb.org") ? active.url : null,
        complaint_excerpts: ctx.complaintExcerpts || null,
        sec_filings: ctx.secFilings || null,
        sec_url: ctx.secUrl || null,
        scope: sc.kind,
        locations: sc.kind === "combined" ? locs.map((l) => ({ location: l.location, bbb_rating: l.bbb_rating, complaints: l.complaints, url: l.url })) : null,
      });
      setFin(data);
      if (sc.kind === "location") setFinCache((c) => ({ ...c, [`${(active.url || "").toLowerCase()}|${(active.location || "").toLowerCase().trim()}`]: data }));
    } catch {
      toast.error("Could not estimate financials for this business.");
    } finally {
      setFinLoading(false);
    }
  };

  const changeScope = (value) => {
    if (!selected) return;
    const sc = value === "__combined__" ? { kind: "combined", loc: null } : { kind: "location", loc: locationsFor(selected)[Number(value)] || selected };
    setScope(sc);
    runFinancials(selected, sc, {
      complaintExcerpts: (reported?.reviews || []).map((r) => r.excerpt).filter(Boolean),
      secFilings: sec?.found ? (sec.filings || []).map((fl) => ({ form: fl.form, filed: fl.filed, description: fl.description, url: fl.url })) : null,
      secUrl: sec?.found ? sec.profile?.edgar_url : null,
    });
  };

  const loadDetails = async (p) => {
    setSelected(p);
    setFin(null);
    setLiveDetails(null);
    setSec(null);
    setLeadAdded(false);
    setScope({ kind: "location", loc: p });
    setComparing(false);
    setCompareData(null);
    setFinLoading(true);
    // SEC / EDGAR registrant lookup — fired in parallel (public companies only).
    setSec({ loading: true });
    const secP = api.post("/sec/company-lookup", { company_name: p.company_name, location: p.location || location || null })
      .then(({ data }) => { setSec(data); return data; })
      .catch(() => { setSec({ found: false, filings: [] }); return null; });
    // Real reported BBB complaints + real owner/contact + tenure when we have a
    // live profile URL — fired in parallel with the AI estimate.
    let reportedP = Promise.resolve(null);
    if (p.url && p.url.includes("bbb.org")) {
      setReported({ loading: true });
      reportedP = api.post("/bbb/reported-reviews", { url: p.url, company_name: p.company_name })
        .then(({ data }) => { setReported({ reviews: data.reviews || [], source_url: data.source_url }); return data.reviews || []; })
        .catch(() => { setReported({ reviews: [], source_url: null }); return []; });
      api.post("/bbb/business-profile-scrape", { url: p.url, company_name: p.company_name })
        .then(({ data }) => { if (data.found) setLiveDetails(data); })
        .catch(() => { /* silent — fall back to AI estimate */ });
    } else {
      setReported(null);
    }
    // Give the real complaint text + SEC filings a moment to arrive so the
    // estimate (and the fraud scan) can cite them as evidence.
    const [reviews, secData] = await Promise.all([withTimeout(reportedP, 25000), withTimeout(secP, 12000)]);
    await runFinancials(p, { kind: "location", loc: p }, {
      complaintExcerpts: (reviews || []).map((r) => r.excerpt).filter(Boolean),
      secFilings: secData?.found ? (secData.filings || []).map((fl) => ({ form: fl.form, filed: fl.filed, description: fl.description, url: fl.url })) : null,
      secUrl: secData?.found ? secData.profile?.edgar_url : null,
    });
  };

  const runNameLookup = async (nameVal, locVal) => {
    const n = (nameVal ?? name).trim();
    const l = (locVal ?? location).trim();
    if (!n) { toast.error("Enter a business name."); return; }
    setSearching(true);
    setCandidates(null); setSelected(null); setFin(null); setReported(null); setNotice(null); setLiveDetails(null); setSec(null);
    try {
      const { data } = await api.post("/bbb/business-lookup", {
        name: n,
        location: l || null,
      });
      const results = data.results || [];
      setCandidates(results);
      setNotice(data.notice || null);
      if (results.length === 0) {
        toast.error("No matching business found on the BBB. Try adding a city/state.");
      } else {
        if (!data.notice) toast.success(`Found ${results.length} match${results.length > 1 ? "es" : ""}.`);
        if (results.length === 1) loadDetails(results[0]);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Lookup failed. Live BBB may not be configured.");
    } finally {
      setSearching(false);
    }
  };

  // When the parent hands us a business to research (from the Outreach Engine),
  // switch to name mode, prefill, and run the lookup automatically (once per request).
  const lastPrefillTs = useRef(null);
  useEffect(() => {
    if (!prefill || !prefill.ts || lastPrefillTs.current === prefill.ts) return;
    lastPrefillTs.current = prefill.ts;
    setMode("name");
    setName(prefill.name || "");
    setLocation(prefill.location || "");
    runNameLookup(prefill.name || "", prefill.location || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefill?.ts]);

  const submit = async (e) => {
    e.preventDefault();
    if (mode === "url") return submitUrl();
    return runNameLookup();
  };

  const submitUrl = async () => {
    if (!website.trim()) { toast.error("Enter a website URL."); return; }
    setSearching(true);
    setCandidates(null); setSelected(null); setFin(null); setReported(null); setNotice(null); setLiveDetails(null); setSec(null);
    try {
      const { data } = await api.post("/bbb/lookup-by-url", { url: website.trim() });
      const results = data.results || [];
      // Reflect what we identified from the site so the estimate uses it.
      if (data.extracted?.industry) setIndustry(data.extracted.industry);
      if (data.extracted?.location) setLocation(data.extracted.location);
      if (data.query) setName(data.query);
      setCandidates(results);
      setNotice(data.notice || null);
      if (results.length === 0) {
        toast.error("Could not identify a business from that website.");
      } else {
        if (!data.notice) toast.success(`Identified ${data.query || "the business"}.`);
        if (results.length === 1) loadDetails(results[0]);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not read that website. Try the name lookup instead.");
    } finally {
      setSearching(false);
    }
  };

  const f = fin?.financials;
  const health = computeHealth(selected, reported);
  const totalExpenses = (f?.expense_breakdown || []).reduce((s, c) => s + (Number(c.amount) || 0), 0) || f?.estimated_annual_expenses || 0;

  // Prefer REAL scraped owner/tenure; fall back to the AI estimate.
  const realContact = liveDetails?.contact || null;
  const aiContact = fin?.contact || null;
  const contact = realContact || aiContact;
  const contactIsEstimate = !realContact && !!aiContact;
  const realTenure = liveDetails?.tenure || null;
  const aiTenure = fin?.tenure || null;
  const tenure = realTenure || aiTenure;
  const tenureIsEstimate = !realTenure && !!aiTenure;
  const plan = fin?.recovery_plan || [];
  const allLocs = locationsFor(selected);
  const activeLoc = scope.kind === "combined" ? null : (scope.loc || selected);
  const scopeLabel = fin?.location_scope || (scope.kind === "combined" ? `All locations combined (${allLocs.length})` : (activeLoc?.location || null));

  // Section numbers that mirror the PDF's numbered sections — computed in the
  // on-screen display order so they stay sequential when a section is absent.
  let _sn = 0;
  const secNumbers = !!f ? (_sn += 1, _sn) : null;
  const secRisk = fin?.fraud_risk ? (_sn += 1, _sn) : null;
  const secPlan = (fin?.recovery_plan || []).length > 0 ? (_sn += 1, _sn) : null;
  const secVoice = reported ? (_sn += 1, _sn) : null;

  const handleLogoPick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file."); return; }
    if (file.size > 2 * 1024 * 1024) { toast.error("Logo must be under 2 MB."); return; }
    const reader = new FileReader();
    reader.onload = () => { setCobrandLogo(reader.result); toast.success("Logo added — it'll appear on the report cover."); };
    reader.onerror = () => toast.error("Could not read that image.");
    reader.readAsDataURL(file);
  };

  const downloadReport = async () => {
    if (!f || !selected) return;
    setDownloading(true);
    try {
      const cats = (f.expense_breakdown || []).map((c) => ({
        label: c.label,
        current: Math.round((Number(c.amount) || 0) / 12),
        projected: Math.round((Number(c.projected ?? (Number(c.amount) || 0) * 0.85)) / 12),
      }));
      const body = {
        company_name: selected.company_name,
        location: (activeLoc?.location) || selected.location || location || null,
        bbb_rating: (activeLoc?.bbb_rating) || selected.bbb_rating || null,
        location_scope: scopeLabel || null,
        inputs_used: fin?.inputs_used || null,
        data_sources: fin?.data_sources || null,
        provenance: fin?.provenance || null,
        fraud_risk: fin?.fraud_risk || null,
        chart_actions: fin?.chart_actions || null,
        industry: industry || null,
        summary: f.summary || null,
        health_score: health ? { score: health.score, grade: health.grade, label: health.label, factors: health.factors } : null,
        recovery_roadmap: plan,
        owner_name: contact?.owner_name || null,
        owner_role: contact?.role || null,
        owner_phone: contact?.phone || null,
        owner_email: contact?.email || null,
        opened: tenure?.opened || null,
        years_in_business: typeof tenure?.years_in_business === "number" ? tenure.years_in_business : null,
        contact_is_estimate: contactIsEstimate,
        financial_snapshot: {
          reported_profit_loss: f.reported_profit_loss || null,
          reported_profit_gain_after: f.recoverable_with_elevare || null,
          loss_drivers: f.loss_drivers || [],
        },
        expense_projection: {
          current_monthly: Math.round((f.estimated_annual_expenses || 0) / 12),
          with_elevare_monthly: cats.reduce((s, c) => s + c.projected, 0),
          categories: cats,
        },
        positive_reviews: fin?.reviews?.positive || [],
        negative_reviews: [
          ...((reported?.reviews || []).map((r) => ({ excerpt: r.excerpt, theme: r.theme || null, source: r.source || "BBB · reported complaint" }))),
          ...((fin?.reviews?.negative || [])),
        ],
        comparison: compareData || null,
        web_analytics: fin?.web_analytics || null,
        survival_risk: fin?.survival_risk || null,
        cobrand_logo: cobrandLogo || null,
      };
      const res = await api.post("/prospect/roadmap-pdf", body, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      const safe = (selected.company_name || "business").replace(/[^A-Za-z0-9_-]+/g, "_");
      a.download = `Elevare_Business_Report_${safe}.pdf`;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      // Defer cleanup so the browser can finish reading the blob (revoking
      // synchronously cancels the download in Safari / some browsers).
      setTimeout(() => { a.remove(); window.URL.revokeObjectURL(url); }, 2000);
      toast.success("Report downloaded.");
    } catch {
      toast.error("Could not build the report PDF.");
    } finally {
      setDownloading(false);
    }
  };

  const CHART_TESTID = { loss_drivers: "chart-loss-drivers", expenses: "chart-expenses", profit_swing: "chart-profit-swing", reviews: "chart-reviews", survival: "chart-survival-risk", expansion: "chart-expansion", fraud: "fraud" };
  const SECTION_TITLE = { loss_drivers: "Where it leaks · loss drivers", expenses: "Expenses · with vs without Elevare", profit_swing: "Profit swing · quarterly goals", reviews: "Reviews · sentiment mix", fraud: "Risk & control review", survival: "Survival risk", expansion: "Expansion roadmap" };
  const finRefFor = () => selected ? `fin:${selected.url || `${selected.company_name}|${selected.location || ""}`}` : null;
  const buildSections = () => {
    const plans = fin?.chart_actions || {};
    const ref0 = finRefFor();
    const out = [];
    Object.keys(SECTION_TITLE).forEach((key) => {
      const p = plans[key];
      if (!p || !(p.actions || []).length) return;
      out.push({
        title: SECTION_TITLE[key], goal: p.goal || "", chart_key: CHART_TESTID[key],
        ref: `${ref0}:${CHART_TESTID[key]}`,
        actions: (p.actions || []).slice(0, 4).map((a) => ({ title: a.title, how: a.how })),
      });
    });
    return out;
  };

  const downloadActionPack = async () => {
    const sections = buildSections();
    if (!sections.length) { toast.error("Run a lookup first — no prioritized actions yet."); return; }
    setPackBusy(true);
    toast.message("Building the action pack — writing every step & checklist…");
    try {
      const res = await api.post("/prospect/action-pack-pdf",
        { business_name: selected?.company_name || "Business", sections }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "elevare-action-pack.pdf"; a.click();
      URL.revokeObjectURL(url);
      const pending = Number(res.headers?.["x-pack-pending"] || 0);
      if (pending > 0) toast.info(`${pending} step guide${pending === 1 ? "" : "s"} still generating — re-download in ~1 min for the complete pack.`, { duration: 7000 });
    } catch { toast.error("Could not build the action pack."); }
    finally { setPackBusy(false); }
  };

  const openConvert = () => {
    setCvEmail(contact?.email || "");
    setCvOwner(contact?.owner_name || selected?.company_name || "");
    setCvPhone(contact?.phone || selected?.phone || "");
    setConvertOpen(true);
  };
  const doConvert = async () => {
    if (!cvEmail.includes("@")) { toast.error("Enter a valid client email."); return; }
    setConvertBusy(true);
    try {
      const { data } = await api.post("/admin/prospect/convert-to-client", {
        email: cvEmail.trim(), owner_name: cvOwner.trim(), phone: cvPhone.trim(),
        business_name: selected?.company_name || "", fin_ref: finRefFor(), sections: buildSections(),
      });
      setConverted(true); setConvertOpen(false);
      toast.success(`${selected?.company_name} is now a client — portal invite ${data.portal_invite_sent ? "sent" : "queued"}, plan with ${data.phases} focus areas carried over.`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not convert to client."); }
    finally { setConvertBusy(false); }
  };

  const addToLeads = async () => {
    if (!selected) return;
    setAddingLead(true);
    try {
      await api.post("/admin/leads/from-prospect", {
        company_name: selected.company_name,
        location: selected.location || location || null,
        bbb_rating: selected.bbb_rating || null,
        complaints: typeof selected.complaints === "number" ? selected.complaints : null,
        industry: industry || null,
        owner_name: contact?.owner_name || null,
        owner_role: contact?.role || null,
        phone: contact?.phone || selected.phone || null,
        email: contact?.email || null,
        summary: f?.summary || null,
        contact_is_estimate: contactIsEstimate,
        accredited: typeof selected.accredited === "boolean" ? selected.accredited : null,
        health_score: health ? { score: health.score, grade: health.grade, label: health.label } : null,
      });
      setLeadAdded(true);
      toast.success(`${selected.company_name} added to Leads.`);
    } catch (e) {
      if (e?.response?.status === 409) {
        setLeadAdded(true);
        toast.info(e?.response?.data?.detail || "Already in your leads.");
      } else {
        toast.error(e?.response?.data?.detail || "Could not add to leads.");
      }
    } finally {
      setAddingLead(false);
    }
  };

  return (
    <div data-testid="business-lookup">
      <div className="rounded-2xl border border-[#1e3d3a]/12 bg-white p-6">
        <div className="flex items-center gap-3">
          <Search className="h-4 w-4 text-[#c9a961]" />
          <span className="text-[10px] uppercase tracking-[0.28em] text-[#1e3d3a]/60">Business lookup</span>
        </div>
        <p className="mt-3 text-sm text-[#1e3d3a]/65 leading-relaxed max-w-2xl">
          Look up a single business by name and location — or paste its website URL — to pull its
          reported BBB profile, an estimated income / profit / loss / expense picture, and live customer reviews.
        </p>

        <div data-testid="lookup-mode" className="mt-5 inline-flex rounded-full border border-[#1e3d3a]/15 bg-[#eae7de]/50 p-1">
          <button
            type="button"
            data-testid="lookup-mode-name"
            onClick={() => setMode("name")}
            className={`rounded-full px-4 py-1.5 text-[11px] uppercase tracking-[0.18em] transition-colors duration-300 ${mode === "name" ? "bg-[#1e3d3a] text-white" : "text-[#1e3d3a]/55 hover:text-[#1e3d3a]"}`}
          >
            By name
          </button>
          <button
            type="button"
            data-testid="lookup-mode-url"
            onClick={() => setMode("url")}
            className={`rounded-full px-4 py-1.5 text-[11px] uppercase tracking-[0.18em] transition-colors duration-300 flex items-center gap-1.5 ${mode === "url" ? "bg-[#1e3d3a] text-white" : "text-[#1e3d3a]/55 hover:text-[#1e3d3a]"}`}
          >
            <Globe className="h-3 w-3" /> By website URL
          </button>
        </div>

        {mode === "name" ? (
          <form onSubmit={submit} data-testid="lookup-form" className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <input data-testid="lookup-name" className={inputCls} value={name} onChange={(e) => setName(e.target.value)} placeholder="Business name" />
            <input data-testid="lookup-location" className={inputCls} value={location} onChange={(e) => setLocation(e.target.value)} placeholder="City, State — e.g. Dallas, TX" />
            <select data-testid="lookup-industry" className={inputCls} value={industry} onChange={(e) => setIndustry(e.target.value)}>
              {INDUSTRIES.map((o) => <option key={o} value={o}>{o || "Industry (auto)"}</option>)}
            </select>
            <button
              type="submit"
              data-testid="lookup-submit"
              disabled={searching}
              className="rounded-full bg-[#1e3d3a] text-white px-6 py-3 text-sm font-semibold hover:bg-[#c9a961] hover:text-[#1e3d3a] transition-colors duration-300 disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {searching ? (<><Loader2 className="h-4 w-4 animate-spin" /> Looking…</>) : (<><Search className="h-4 w-4" /> Look up</>)}
            </button>
          </form>
        ) : (
          <form onSubmit={submit} data-testid="lookup-url-form" className="mt-6 grid grid-cols-1 lg:grid-cols-4 gap-3">
            <div className="lg:col-span-3 relative">
              <Globe className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-[#1e3d3a]/35" />
              <input
                data-testid="lookup-website"
                className={`${inputCls} pl-11`}
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                placeholder="Company website — e.g. acmeplumbing.com"
                inputMode="url"
                autoCapitalize="none"
                autoCorrect="off"
              />
            </div>
            <button
              type="submit"
              data-testid="lookup-url-submit"
              disabled={searching}
              className="rounded-full bg-[#1e3d3a] text-white px-6 py-3 text-sm font-semibold hover:bg-[#c9a961] hover:text-[#1e3d3a] transition-colors duration-300 disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {searching ? (<><Loader2 className="h-4 w-4 animate-spin" /> Reading site…</>) : (<><Search className="h-4 w-4" /> Look up</>)}
            </button>
          </form>
        )}
      </div>

      {notice && (
        <div data-testid="lookup-notice" className="mt-4 rounded-xl border border-[#c9a961]/40 bg-[#c9a961]/10 px-4 py-3 text-xs text-[#a3823f]">
          {notice}
        </div>
      )}

      {candidates && candidates.length > 1 && !selected && (
        <div className="mt-6" data-testid="lookup-candidates">
          <span className="text-[10px] uppercase tracking-[0.22em] text-[#1e3d3a]/55">Which one?</span>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {candidates.map((p, i) => <ProfileCard key={i} p={p} onPick={loadDetails} active={false} />)}
          </div>
        </div>
      )}

      {selected && (
        <motion.div
          data-testid="lookup-result"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
          className="mt-6 space-y-4"
        >
          {/* Profile header */}
          <div data-testid="lookup-profile" className="rounded-2xl border border-[#c9a961]/40 bg-[#c9a961]/8 p-6">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="min-w-0">
                <h3 className="font-head text-2xl text-[#1e3d3a] flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-[#a3823f]" /> {selected.company_name}
                </h3>
                <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-[#1e3d3a]/60">
                  {selected.location && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {selected.location}</span>}
                  {selected.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" /> {selected.phone}</span>}
                  {selected.accredited && <span className="flex items-center gap-1 text-[#4b7a3a]"><ShieldCheck className="h-3 w-3" /> BBB Accredited</span>}
                  {(selected.complaints !== null && selected.complaints !== undefined) && (
                    <span className="flex items-center gap-1 text-[#b23a48]"><MessageSquareWarning className="h-3 w-3" /> {selected.complaints} complaints on file</span>
                  )}
                  {tenure && (tenure.years_in_business || tenure.opened) && (
                    <span data-testid="lookup-tenure" className="flex items-center gap-1 text-[#1e3d3a]/70">
                      <CalendarClock className="h-3 w-3" />
                      {tenure.years_in_business ? `Open ${tenure.years_in_business} yr${tenure.years_in_business === 1 ? "" : "s"}` : "Open"}
                      {tenure.opened ? ` · since ${tenure.opened}` : ""}
                      {tenureIsEstimate ? " (est.)" : ""}
                    </span>
                  )}
                </div>
                {selected.url && selected.url.includes("bbb.org") && (
                  <a href={selected.url} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.18em] text-[#a3823f] hover:text-[#1e3d3a] transition-colors duration-300">
                    View BBB profile <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </div>
              <div className="flex flex-col items-end gap-3 shrink-0">
                <div className="flex items-start gap-5">
                  <HealthGauge data={health} />
                  <div className="text-center">
                    <div className="text-[9px] uppercase tracking-[0.2em] text-[#1e3d3a]/45">BBB rating</div>
                    <div className="mt-1 font-head text-3xl text-[#1e3d3a]">{selected.bbb_rating || "NR"}</div>
                  </div>
                </div>
                <button
                  type="button"
                  data-testid="lookup-download"
                  onClick={downloadReport}
                  disabled={!f || downloading}
                  className="inline-flex items-center gap-1.5 rounded-full bg-[#1e3d3a] text-white px-4 py-2 text-[11px] uppercase tracking-[0.18em] hover:bg-[#c9a961] hover:text-[#1e3d3a] transition-colors duration-300 disabled:opacity-50"
                >
                  {downloading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileDown className="h-3.5 w-3.5" />}
                  {downloading ? "Building…" : "Download report"}
                </button>
                <button
                  type="button"
                  data-testid="lookup-add-lead"
                  onClick={addToLeads}
                  disabled={addingLead || leadAdded}
                  className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-[11px] uppercase tracking-[0.18em] transition-colors duration-300 disabled:opacity-70 ${
                    leadAdded
                      ? "bg-[#4b7a3a]/15 text-[#4b7a3a] cursor-default"
                      : "border border-[#c9a961] bg-white text-[#a3823f] hover:bg-[#c9a961] hover:text-[#1e3d3a]"
                  }`}
                >
                  {addingLead ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    : leadAdded ? <Check className="h-3.5 w-3.5" />
                    : <UserPlus className="h-3.5 w-3.5" />}
                  {leadAdded ? "Added to leads" : addingLead ? "Adding…" : "Add to leads"}
                </button>
                <button
                  type="button"
                  data-testid="lookup-action-pack"
                  onClick={downloadActionPack}
                  disabled={!f || packBusy}
                  className="inline-flex items-center gap-1.5 rounded-full border border-[#1e3d3a]/20 bg-white px-4 py-2 text-[11px] uppercase tracking-[0.18em] text-[#1e3d3a] hover:border-[#c9a961] hover:text-[#a3823f] transition-colors duration-300 disabled:opacity-50"
                >
                  {packBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />}
                  {packBusy ? "Building…" : "Action pack"}
                </button>
                <button
                  type="button"
                  data-testid="lookup-convert-client"
                  onClick={openConvert}
                  disabled={!f || converted}
                  className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-[11px] uppercase tracking-[0.18em] transition-colors duration-300 disabled:opacity-70 ${
                    converted ? "bg-[#4b7a3a]/15 text-[#4b7a3a] cursor-default"
                      : "bg-[#c9a961] text-[#1e3d3a] hover:bg-[#1e3d3a] hover:text-white"
                  }`}
                >
                  {converted ? <Check className="h-3.5 w-3.5" /> : <UserPlus className="h-3.5 w-3.5" />}
                  {converted ? "Client created" : "Convert to client"}
                </button>
                <input ref={logoInputRef} type="file" accept="image/*" className="hidden" data-testid="lookup-logo-input" onChange={handleLogoPick} />
                {cobrandLogo ? (
                  <div className="flex items-center gap-1.5" data-testid="lookup-logo-chip">
                    <img src={cobrandLogo} alt="Co-brand logo preview" className="h-5 w-auto max-w-[64px] rounded border border-[#c9a961]/50 bg-white object-contain" />
                    <span className="text-[9px] uppercase tracking-[0.16em] text-[#4b7a3a]">Logo added</span>
                    <button
                      type="button"
                      data-testid="lookup-logo-remove"
                      onClick={() => { setCobrandLogo(null); if (logoInputRef.current) logoInputRef.current.value = ""; }}
                      className="text-[#1e3d3a]/40 hover:text-[#b23a48] transition-colors"
                      aria-label="Remove logo"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    data-testid="lookup-logo-add"
                    onClick={() => logoInputRef.current?.click()}
                    className="inline-flex items-center gap-1.5 rounded-full border border-[#1e3d3a]/20 bg-white px-3 py-1.5 text-[10px] uppercase tracking-[0.16em] text-[#1e3d3a]/55 hover:border-[#c9a961] hover:text-[#a3823f] transition-colors duration-300"
                  >
                    <ImagePlus className="h-3 w-3" /> Add logo
                  </button>
                )}
              </div>
            </div>
            {candidates && candidates.length > 1 && (
              <button
                type="button"
                data-testid="lookup-change"
                onClick={() => { setSelected(null); setFin(null); setReported(null); }}
                className="mt-3 text-[10px] uppercase tracking-[0.18em] text-[#1e3d3a]/45 hover:text-[#1e3d3a] transition-colors duration-300"
              >
                ← Pick a different match
              </button>
            )}
          </div>

          {/* Social media presence — real deep-links to each platform */}
          {(selected.social_media || fin?.social_media)?.length > 0 && (
            <div data-testid="lookup-social" className="rounded-2xl border border-[#1e3d3a]/12 bg-white p-5">
              <p className="text-[10px] uppercase tracking-[0.24em] text-[#1e3d3a]/55 flex items-center gap-1.5 mb-3">
                <Share2 className="h-3.5 w-3.5 text-[#c9a961]" /> Social media &amp; web presence
              </p>
              <div className="flex flex-wrap gap-2">
                {(selected.social_media || fin?.social_media || []).map((s) => (
                  <a
                    key={s.platform}
                    data-testid={`social-${s.platform.toLowerCase()}`}
                    href={s.url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 rounded-full border border-[#1e3d3a]/15 bg-[#eae7de]/50 px-3 py-1.5 text-[11px] text-[#1e3d3a] hover:border-[#c9a961] hover:text-[#a3823f] transition-colors duration-300"
                  >
                    {s.platform === "Website" ? <Globe className="h-3 w-3" /> : <ExternalLink className="h-3 w-3" />}
                    {s.platform}
                  </a>
                ))}
              </div>
              <p className="mt-2 text-[10px] text-[#1e3d3a]/40">Links open a live search on each platform for this business — click to confirm and grab the real account.</p>
            </div>
          )}

          {/* Locations owned under the same business — pick one or combine all */}
          {allLocs.length > 1 && (
            <div data-testid="lookup-alt-locations" className="rounded-2xl border border-[#c9a961]/40 bg-white p-5">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <p className="text-[10px] uppercase tracking-[0.24em] text-[#1e3d3a]/55 flex items-center gap-1.5">
                  <MapPin className="h-3.5 w-3.5 text-[#c9a961]" /> {allLocs.length} locations · choose which to analyze
                </p>
                <div className="flex items-center gap-2">
                <button type="button" data-testid="compare-toggle" onClick={() => setComparing((v) => !v)}
                  className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[10px] uppercase tracking-[0.14em] transition-colors duration-300 ${comparing ? "border border-[#1e3d3a]/15 text-[#1e3d3a]/55" : "bg-[#1e3d3a] text-white hover:bg-[#c9a961] hover:text-[#1e3d3a]"}`}>
                  <GitCompare className="h-3 w-3" /> {comparing ? "Close compare" : "Compare two"}
                </button>
                <select
                  data-testid="lookup-location-scope"
                  value={scope.kind === "combined" ? "__combined__" : String(Math.max(0, allLocs.indexOf(scope.loc || selected)))}
                  onChange={(e) => changeScope(e.target.value)}
                  disabled={finLoading}
                  className="rounded-full border border-[#1e3d3a]/15 bg-[#eae7de]/60 px-3 py-1.5 text-xs text-[#1e3d3a] focus:border-[#c9a961] focus:outline-none disabled:opacity-60"
                >
                  {allLocs.map((l, i) => (
                    <option key={l.url || l.location || i} value={String(i)}>
                      {l.location || l.company_name}{l.bbb_rating ? ` · BBB ${l.bbb_rating}` : ""}{l.complaints != null ? ` · ${l.complaints} complaints` : ""}
                    </option>
                  ))}
                  <option value="__combined__">All locations combined ({allLocs.length})</option>
                </select>
                </div>
              </div>
              {comparing && (
                <div className="mt-4">
                  <LocationCompare company={selected.company_name} industry={industry} locations={allLocs} cache={finCache}
                    onCache={(k, d) => setFinCache((c) => ({ ...c, [k]: d }))} onClose={() => setComparing(false)} onCompare={setCompareData} />
                </div>
              )}
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                {allLocs.map((a, i) => {
                  const on = scope.kind === "location" && (scope.loc || selected) === a;
                  return (
                    <button key={a.url || i} type="button" data-testid={`alt-location-${i}`} onClick={() => changeScope(String(i))} disabled={finLoading}
                      className={`flex items-center justify-between gap-3 rounded-xl border px-3 py-2 text-left transition-colors duration-300 ${on ? "border-[#1e3d3a] bg-[#1e3d3a] text-white" : "border-[#1e3d3a]/10 bg-[#eae7de]/40 hover:border-[#c9a961]"}`}>
                      <div className="min-w-0">
                        <p className="text-sm truncate">{a.location || a.company_name}</p>
                        <p className={`text-[10px] ${on ? "text-[#c9a961]" : "text-[#1e3d3a]/45"}`}>
                          {a.bbb_rating ? `BBB ${a.bbb_rating}` : "Unrated"}{a.complaints != null ? ` · ${a.complaints} complaints` : ""}{a.phone ? ` · ${a.phone}` : ""}
                        </p>
                      </div>
                      {a.url && (
                        <a href={a.url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className={`shrink-0 ${on ? "text-[#c9a961]" : "text-[#a3823f]"} hover:opacity-70`} title="Open BBB profile">
                          <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Financials */}
          {finLoading && (
            <div className="rounded-2xl border border-[#1e3d3a]/12 bg-white p-10 flex flex-col items-center justify-center gap-3">
              <Loader2 className="h-7 w-7 text-[#c9a961] animate-spin" />
              <p className="text-sm text-[#1e3d3a]/60">Estimating income, profit &amp; expenses{scopeLabel ? ` · ${scopeLabel}` : ""}…</p>
              <p className="text-[11px] text-[#1e3d3a]/40">Pulling real BBB complaint text and SEC filings first so the estimate and fraud scan can cite them.</p>
            </div>
          )}
          {f && (
            <div data-testid="lookup-financials" className="space-y-4">
              {secNumbers && <SectionNo n={secNumbers}>The Numbers</SectionNo>}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <StatTile label="Est. annual income" value={money(f.estimated_annual_revenue)} icon={Wallet} tone="neutral" />
                <StatTile label="Est. annual expenses" value={money(f.estimated_annual_expenses)} icon={Receipt} tone="neutral" />
                <StatTile
                  label="Est. net profit / loss"
                  value={money(f.estimated_net_profit)}
                  icon={(f.estimated_net_profit || 0) >= 0 ? TrendingUp : TrendingDown}
                  tone={(f.estimated_net_profit || 0) >= 0 ? "pos" : "neg"}
                  sub={typeof f.profit_margin_pct === "number" ? `${f.profit_margin_pct}% margin` : null}
                />
                <StatTile
                  label="Leaking / yr"
                  value={money(f.reported_profit_loss)}
                  icon={MessageSquareWarning}
                  tone="neg"
                  sub={f.recoverable_with_elevare ? `${money(f.recoverable_with_elevare)} recoverable` : null}
                />
              </div>

              {f.summary && (
                <p className="text-sm leading-relaxed text-[#1e3d3a]/75 rounded-2xl border border-[#1e3d3a]/12 bg-white p-5">{f.summary}</p>
              )}

              <ProspectCharts fin={fin} reported={reported} bbbUrl={activeLoc?.url || selected.url} scopeLabel={scopeLabel}
                finRef={`fin:${selected.url || `${selected.company_name}|${selected.location || ""}`}`}
                businessName={selected.company_name} />
              {secRisk && <SectionNo n={secRisk}>Risk &amp; Control Review</SectionNo>}
              <FraudRiskCard risk={fin?.fraud_risk} plan={fin?.chart_actions?.fraud}
                finRef={`fin:${selected.url || `${selected.company_name}|${selected.location || ""}`}`}
                businessName={selected.company_name} />

              {(f.expense_breakdown || []).length > 0 && (
                <div data-testid="lookup-expense-breakdown" className="rounded-2xl border border-[#1e3d3a]/12 bg-white p-5">
                  <span className="text-[10px] uppercase tracking-[0.22em] text-[#1e3d3a]/55 flex items-center gap-1.5">
                    <Receipt className="h-3.5 w-3.5 text-[#a3823f]" /> Estimated expense breakdown
                  </span>
                  <div className="mt-4 space-y-3">
                    {f.expense_breakdown.map((c, i) => {
                      const pct = totalExpenses ? Math.round((Number(c.amount) / totalExpenses) * 100) : 0;
                      return (
                        <div key={i}>
                          <div className="flex items-center justify-between text-xs text-[#1e3d3a]/70">
                            <span>{c.label}</span>
                            <span className="tabular-nums">{money(c.amount)} · {pct}%</span>
                          </div>
                          <div className="mt-1 h-2 rounded-full bg-[#eae7de] overflow-hidden">
                            <div className="h-full rounded-full bg-[#c9a961]" style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Owner / contact */}
          {contact && (contact.owner_name || contact.phone || contact.email) && (
            <div data-testid="lookup-contact" className="rounded-2xl border border-[#1e3d3a]/12 bg-white p-5">
              <span className="text-[10px] uppercase tracking-[0.22em] text-[#1e3d3a]/55 flex items-center gap-1.5">
                <User className="h-3.5 w-3.5 text-[#a3823f]" /> Owner &amp; contact
                {contactIsEstimate && <span className="ml-1 rounded-full bg-[#c9a961]/20 px-2 py-0.5 text-[9px] tracking-[0.14em] text-[#a3823f]">AI estimate — verify</span>}
              </span>
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
                {contact.owner_name && (
                  <div className="rounded-xl border border-[#1e3d3a]/10 bg-[#eae7de]/40 px-4 py-3">
                    <div className="text-[9px] uppercase tracking-[0.2em] text-[#1e3d3a]/45">Owner</div>
                    <div className="mt-1 text-sm text-[#1e3d3a] font-medium">{contact.owner_name}</div>
                    {contact.role && <div className="text-[11px] text-[#1e3d3a]/50">{contact.role}</div>}
                  </div>
                )}
                {contact.phone && (
                  <a href={`tel:${contact.phone.replace(/[^0-9+]/g, "")}`} data-testid="lookup-contact-phone" className="rounded-xl border border-[#1e3d3a]/10 bg-[#eae7de]/40 px-4 py-3 hover:border-[#c9a961] transition-colors duration-300 block">
                    <div className="text-[9px] uppercase tracking-[0.2em] text-[#1e3d3a]/45 flex items-center gap-1"><Phone className="h-3 w-3" /> Phone</div>
                    <div className="mt-1 text-sm text-[#1e3d3a] font-medium">{contact.phone}</div>
                  </a>
                )}
                {contact.email && (
                  <a href={`mailto:${contact.email}`} data-testid="lookup-contact-email" className="rounded-xl border border-[#1e3d3a]/10 bg-[#eae7de]/40 px-4 py-3 hover:border-[#c9a961] transition-colors duration-300 block">
                    <div className="text-[9px] uppercase tracking-[0.2em] text-[#1e3d3a]/45 flex items-center gap-1"><Mail className="h-3 w-3" /> Registered email</div>
                    <div className="mt-1 text-sm text-[#1e3d3a] font-medium break-all">{contact.email}</div>
                  </a>
                )}
              </div>
            </div>
          )}

          {/* Prebuilt recovery plan */}
          {plan.length > 0 && (
            <div data-testid="lookup-plan" className="rounded-2xl border border-[#1e3d3a]/12 bg-white p-5">
              {secPlan && <div className="mb-3"><SectionNo n={secPlan}>The Plan</SectionNo></div>}
              <span className="text-[10px] uppercase tracking-[0.22em] text-[#1e3d3a]/55 flex items-center gap-1.5">
                <ClipboardList className="h-3.5 w-3.5 text-[#a3823f]" /> Prebuilt recovery plan
              </span>
              <div className="mt-4 relative pl-8">
                <div className="absolute left-[7px] top-2 bottom-2 w-px bg-[#1e3d3a]/10" />
                {plan.map((ph, i) => (
                  <div key={i} data-testid={`lookup-plan-phase-${i}`} className="relative mb-5">
                    <span className="absolute -left-8 top-1.5 h-4 w-4 rounded-full border-2 border-[#c9a961] bg-white" />
                    <div className="rounded-2xl border border-[#1e3d3a]/12 bg-[#eae7de]/30 p-5">
                      <div className="flex items-center justify-between gap-3">
                        <h4 className="font-head text-lg text-[#1e3d3a]">{ph.phase}</h4>
                        {ph.timeframe && <span className="text-xs text-[#1e3d3a]/45">{ph.timeframe}</span>}
                      </div>
                      {ph.objective && <p className="mt-2 text-sm text-[#1e3d3a]/65">{ph.objective}</p>}
                      <ul className="mt-3 space-y-2">
                        {(ph.steps || []).map((st, j) => (
                          <li key={j} className="flex items-start gap-2 text-sm text-[#1e3d3a]/75">
                            <CheckCircle2 className="h-4 w-4 text-[#4b7a3a] shrink-0 mt-0.5" /> {st}
                          </li>
                        ))}
                      </ul>
                      {ph.milestone && <p className="mt-3 text-xs text-[#a3823f]">Milestone: {ph.milestone}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Reported (real) BBB complaints */}
          {reported && (
            <div data-testid="lookup-reported" className="rounded-2xl border border-[#b23a48]/25 bg-white p-5">
              {secVoice && <div className="mb-3"><SectionNo n={secVoice}>Voice of the Customer</SectionNo></div>}
              <span className="text-[10px] uppercase tracking-[0.22em] text-[#b23a48] flex items-center gap-1.5">
                <MessageSquareWarning className="h-3.5 w-3.5" /> Reported through the BBB · live
              </span>
              {reported.loading && (
                <p className="mt-3 text-xs text-[#1e3d3a]/55 flex items-center gap-2">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Pulling live complaints from this business's BBB profile…
                </p>
              )}
              {!reported.loading && (reported.reviews || []).length === 0 && (
                <p className="mt-3 text-xs text-[#1e3d3a]/50">No complaint text could be pulled from this profile's BBB pages.</p>
              )}
              {!reported.loading && (reported.reviews || []).length > 0 && (
                <div className="mt-3 space-y-2">
                  {reported.reviews.map((r, i) => <ReviewCard key={i} r={{ ...r, source: r.source || "BBB · reported" }} tone="neg" />)}
                  {reported.source_url && <p className="text-[9px] text-[#1e3d3a]/35 break-all">Source: {reported.source_url}</p>}
                </div>
              )}
            </div>
          )}

          {/* SEC · EDGAR registrant filings */}
          {sec && (
            <div data-testid="lookup-sec" className="rounded-2xl border border-[#1e3d3a]/12 bg-white p-5">
              <span className="text-[10px] uppercase tracking-[0.22em] text-[#1e3d3a]/55 flex items-center gap-1.5">
                <Landmark className="h-3.5 w-3.5 text-[#a3823f]" /> SEC · EDGAR
              </span>
              {sec.loading && (
                <p className="mt-3 text-xs text-[#1e3d3a]/55 flex items-center gap-2">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Checking the SEC's EDGAR registrant database…
                </p>
              )}
              {!sec.loading && !sec.found && (
                <p className="mt-3 text-xs text-[#1e3d3a]/50">
                  No SEC filings found for this business — expected for private companies, which aren't SEC registrants.
                </p>
              )}
              {!sec.loading && sec.found && sec.profile && (
                <div className="mt-3 space-y-4">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div className="rounded-xl border border-[#1e3d3a]/10 bg-[#eae7de]/40 px-3 py-2">
                      <div className="text-[9px] uppercase tracking-[0.18em] text-[#1e3d3a]/45">Registrant</div>
                      <div className="mt-1 text-xs text-[#1e3d3a] font-medium">{sec.profile.name}</div>
                    </div>
                    <div className="rounded-xl border border-[#1e3d3a]/10 bg-[#eae7de]/40 px-3 py-2">
                      <div className="text-[9px] uppercase tracking-[0.18em] text-[#1e3d3a]/45">CIK</div>
                      <div className="mt-1 text-xs text-[#1e3d3a] font-medium tabular-nums">{sec.profile.cik}</div>
                    </div>
                    {sec.profile.sic_description && (
                      <div className="rounded-xl border border-[#1e3d3a]/10 bg-[#eae7de]/40 px-3 py-2">
                        <div className="text-[9px] uppercase tracking-[0.18em] text-[#1e3d3a]/45">Industry (SIC)</div>
                        <div className="mt-1 text-xs text-[#1e3d3a] font-medium">{sec.profile.sic_description}</div>
                      </div>
                    )}
                    {(sec.profile.tickers?.length > 0 || sec.profile.state_of_incorporation) && (
                      <div className="rounded-xl border border-[#1e3d3a]/10 bg-[#eae7de]/40 px-3 py-2">
                        <div className="text-[9px] uppercase tracking-[0.18em] text-[#1e3d3a]/45">
                          {sec.profile.tickers?.length > 0 ? "Ticker" : "State of inc."}
                        </div>
                        <div className="mt-1 text-xs text-[#1e3d3a] font-medium">
                          {sec.profile.tickers?.length > 0 ? sec.profile.tickers.join(", ") : sec.profile.state_of_incorporation}
                        </div>
                      </div>
                    )}
                  </div>
                  {sec.profile.address && (
                    <p className="text-[11px] text-[#1e3d3a]/50">Registered address: {sec.profile.address}</p>
                  )}
                  {(sec.filings || []).length > 0 && (
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.18em] text-[#1e3d3a]/45 mb-2">Recent filings</p>
                      <div className="space-y-1.5">
                        {sec.filings.map((fl, i) => (
                          <a
                            key={i}
                            data-testid={`sec-filing-${i}`}
                            href={fl.url}
                            target="_blank"
                            rel="noreferrer"
                            className="flex items-center justify-between gap-3 rounded-xl border border-[#1e3d3a]/10 bg-white px-3 py-2 hover:border-[#c9a961] transition-colors duration-300 group"
                          >
                            <div className="flex items-center gap-2 min-w-0">
                              <FileText className="h-3.5 w-3.5 text-[#a3823f] shrink-0" />
                              <span className="text-xs font-medium text-[#1e3d3a] shrink-0">{fl.form}</span>
                              <span className="text-[11px] text-[#1e3d3a]/50 truncate">{fl.description}</span>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              {fl.filed && <span className="text-[10px] text-[#1e3d3a]/40 tabular-nums">{fl.filed}</span>}
                              <ExternalLink className="h-3 w-3 text-[#1e3d3a]/40 group-hover:text-[#a3823f]" />
                            </div>
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                  {sec.profile.edgar_url && (
                    <a href={sec.profile.edgar_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.16em] text-[#a3823f] hover:text-[#1e3d3a] transition-colors duration-300">
                      View full EDGAR profile <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Customer feedback themes (from public signals — shown as themes, not fabricated quotes) */}
          {fin?.reviews && ((fin.reviews.negative || []).length > 0 || (fin.reviews.positive || []).length > 0) && (() => {
            const uniq = (arr) => [...new Set((arr || []).map((r) => (r.theme || "").trim()).filter(Boolean))].slice(0, 8);
            const negT = uniq(fin.reviews.negative);
            const posT = uniq(fin.reviews.positive);
            if (!negT.length && !posT.length) return null;
            const Chip = ({ label, tone }) => (
              <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs ${tone === "neg" ? "bg-[#b23a48]/8 text-[#b23a48] border border-[#b23a48]/20" : "bg-[#4b7a3a]/8 text-[#4b7a3a] border border-[#4b7a3a]/20"}`}>{label}</span>
            );
            return (
              <div data-testid="lookup-reviews" className="rounded-2xl border border-[#1e3d3a]/12 bg-white p-5">
                <span className="text-[10px] uppercase tracking-[0.22em] text-[#1e3d3a]/55">Customer feedback themes</span>
                <p className="mt-1 text-xs text-[#1e3d3a]/50">Recurring themes from public signals — shown as themes, to be confirmed against real reviews during the diagnostic.</p>
                {negT.length > 0 && (
                  <div className="mt-3">
                    <p className="text-[10px] uppercase tracking-[0.18em] text-[#b23a48] flex items-center gap-1.5">
                      <MessageSquareWarning className="h-3 w-3" /> Areas to address
                    </p>
                    <div className="mt-2 flex flex-wrap gap-2" data-testid="reviews-neg-themes">
                      {negT.map((t, i) => <Chip key={i} label={t} tone="neg" />)}
                    </div>
                  </div>
                )}
                {posT.length > 0 && (
                  <div className="mt-4">
                    <p className="text-[10px] uppercase tracking-[0.18em] text-[#4b7a3a] flex items-center gap-1.5">
                      <ThumbsUp className="h-3 w-3" /> Working well
                    </p>
                    <div className="mt-2 flex flex-wrap gap-2" data-testid="reviews-pos-themes">
                      {posT.map((t, i) => <Chip key={i} label={t} tone="pos" />)}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
        </motion.div>
      )}

      {convertOpen && (
        <div className="fixed inset-0 z-[130] flex items-center justify-center bg-black/40 p-4" data-testid="convert-modal" onClick={() => setConvertOpen(false)}>
          <div className="w-full max-w-md rounded-2xl bg-white p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-head text-xl text-[#1e3d3a]">Convert to client</h3>
              <button data-testid="convert-close" onClick={() => setConvertOpen(false)} className="text-[#1e3d3a]/40 hover:text-[#1e3d3a]"><X className="h-5 w-5" /></button>
            </div>
            <p className="text-sm text-[#1e3d3a]/60 mb-4">Creates {selected?.company_name}'s portal, carries their prioritized actions and any ticked progress into a live recovery plan, and emails a portal invite.</p>
            <label className="block text-[10px] uppercase tracking-wide text-[#1e3d3a]/50 mb-1">Client email (portal login)</label>
            <input data-testid="convert-email" type="email" value={cvEmail} onChange={(e) => setCvEmail(e.target.value)} placeholder="owner@business.com" className={inputCls + " mb-3"} />
            <label className="block text-[10px] uppercase tracking-wide text-[#1e3d3a]/50 mb-1">Owner name</label>
            <input data-testid="convert-owner" value={cvOwner} onChange={(e) => setCvOwner(e.target.value)} className={inputCls + " mb-3"} />
            <label className="block text-[10px] uppercase tracking-wide text-[#1e3d3a]/50 mb-1">Phone (optional)</label>
            <input data-testid="convert-phone" value={cvPhone} onChange={(e) => setCvPhone(e.target.value)} className={inputCls} />
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setConvertOpen(false)} className="rounded-full border border-[#1e3d3a]/20 px-4 py-2 text-sm text-[#1e3d3a]/70">Cancel</button>
              <button data-testid="convert-submit" disabled={convertBusy} onClick={doConvert}
                className="inline-flex items-center gap-1.5 rounded-full bg-[#c9a961] px-4 py-2 text-sm font-semibold text-[#1a2e2b] hover:bg-[#d8b972] disabled:opacity-50">
                {convertBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />} Create client
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
